import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE } from '../../constants/gaEnums';
import { useSearchParams } from 'react-router-dom';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';
import PageLayout from '../../components/layout/PageLayout';
import NoData from '../../components/common/NoData';
import PageTitle from '../../components/common/PageTitle';
import SidebarWideAd from '../../components/ad/SidebarWideAd';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { notificationApi } from '../../api/notification';
import { useNotificationStore } from '../../stores/notificationStore';
import NotificationItem from './NotificationItem';
import BasicButton from '../../components/button/BasicButton';

const ITEMS_PER_PAGE = 10;

const Notifications = () => {
  useTrackPageView({ pageType: PAGE_TYPE.NOTIFICATION });
  const [searchParams, setSearchParams] = useSearchParams();
  const [allNotifications, setAllNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const decrementUnreadCount = useNotificationStore((state) => state.decrementUnreadCount);
  const resetUnreadCount = useNotificationStore((state) => state.resetUnreadCount);

  const currentPage = parseInt(searchParams.get('page')) || 1;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(allNotifications.length / ITEMS_PER_PAGE)),
    [allNotifications.length],
  );

  const pageNotifications = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allNotifications.slice(start, start + ITEMS_PER_PAGE);
  }, [allNotifications, currentPage]);

  const hasUnreadNotifications = useMemo(
    () => allNotifications.some((notification) => !notification.isRead),
    [allNotifications],
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const response = await notificationApi.getNotifications();
        setAllNotifications(response.data || []);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  // 삭제로 현재 페이지가 빈 경우 이전 페이지로 이동
  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      setSearchParams({ page: totalPages.toString() });
    }
  }, [currentPage, totalPages, loading, setSearchParams]);

  const handlePageChange = (event, newPage) => {
    setSearchParams({ page: newPage.toString() });
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationApi.markAsRead(id);
      setAllNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      decrementUnreadCount();
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      const notification = allNotifications.find((n) => n.id === id);
      await notificationApi.deleteNotification(id);
      setAllNotifications((prev) => prev.filter((n) => n.id !== id));
      if (notification && !notification.isRead) {
        decrementUnreadCount();
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!hasUnreadNotifications) return;

    try {
      await notificationApi.markAllAsRead();
      setAllNotifications((prev) => prev.map((notification) => ({ ...notification, isRead: true })));
      resetUnreadCount();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  return (
    <PageLayout>
      <SidebarLayout sidebar={<SidebarWideAd pageType={PAGE_TYPE.NOTIFICATION} />}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: { xs: '16px', md: '30px', lg: '40px' },
          }}
        >
          <PageTitle sx={{ mb: 0 }}>내 알림 목록</PageTitle>
          <BasicButton
            variant="outlined"
            onClick={handleMarkAllAsRead}
            disabled={!hasUnreadNotifications}
            sx={{
              flexShrink: 0,
              height: { xs: '34px', md: '36px' },
              px: { xs: 1.5, md: 2 },
              py: 0,
              fontSize: { xs: '12px', md: '14px' },
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            모두 읽음 처리
          </BasicButton>
        </Box>
        <ProgressBar isLoading={loading}>
          {allNotifications.length === 0 ? (
            <NoData message="알림이 없습니다" />
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {pageNotifications.map((notification, index) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    isFirst={index === 0}
                  />
                ))}
              </Box>

              {totalPages > 1 && (
                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
              )}
            </>
          )}
        </ProgressBar>
      </SidebarLayout>
    </PageLayout>
  );
};

export default Notifications;
